<script setup>
import GenerateProductManagementFeedAction from '@frontJs/components/actions/projectManagement/sea/GenerateProductManagementFeedAction.vue';
import ResetProductManagementItemsAction from '@frontJs/components/actions/projectManagement/sea/ResetProductManagementItemsAction.vue';
import UpdateCpcProductManagementAction from '@frontJs/components/actions/projectManagement/sea/UpdateCpcProductManagementAction.vue';
import ArrowsClockwiseIcon from '@iconify-vue/ph/arrows-clockwise';
import CopySimpleIcon from '@iconify-vue/ph/copy-simple';
import CurrencyEurIcon from '@iconify-vue/ph/currency-eur';
import DownloadSimpleIcon from '@iconify-vue/ph/download-simple';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import RecycleIcon from '@iconify-vue/ph/recycle';
import DisplayLineChartAction from '@js/components/actions/DisplayLineChartAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import LinkButtonCell from '@js/components/tables/cells/LinkButtonCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { toastSuccess } from '@js/utils/toast';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { tenant } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
    updateData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'id',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'ID',
                    width: 0,
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.id,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'cms_store_id',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Magasin',
                }),
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.eCommerceStore.name,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'feedUrl',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Flux<br/>Produit',
                    width: 0,
                }),
            cell: (props) =>
                props.row.original.productFeedUrl &&
                props.row.original.generateProductFeed
                    ? h(LinkButtonCell, {
                          href: props.row.original.productFeedUrl,
                          target: '_blank',
                      })
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'feedUrl',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Flux<br/>CPC',
                    width: 0,
                }),
            cell: (props) =>
                props.row.original.cpcFeedUrl &&
                props.row.original.generateProductFeed
                    ? h(
                          BaseCell,
                          {},
                          {
                              default: () => [
                                  h(ActionButton, {
                                      href: props.row.original.cpcFeedUrl,
                                      target: '_blank',
                                      type: 'secondary',
                                      size: 'small',
                                      square: true,
                                      icon: DownloadSimpleIcon,
                                  }),
                                  h(ActionButton, {
                                      callback: () => {
                                          navigator.clipboard.writeText(
                                              props.row.original.cpcFeedUrl,
                                          );

                                          toastSuccess('Copié avec succès.');
                                      },
                                      target: '_blank',
                                      type: 'secondary',
                                      size: 'small',
                                      square: true,
                                      icon: CopySimpleIcon,
                                  }),
                              ],
                          },
                      )
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'number_of_products',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Produits<br/>détectés',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.numberOfProducts,
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'products',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Produits<br/>générés',
                }),
            cell: (props) =>
                props.row.original.numberOfItems !== null
                    ? h(
                          NumberCell,
                          {
                              number: props.row.original.numberOfItems,
                          },
                          props.row.original.numberOfItemsChartData
                              ? {
                                    actions: () =>
                                        h(DisplayLineChartAction, {
                                            labels: props.row.original
                                                .numberOfItemsChartData.labels,
                                            datasets: [
                                                {
                                                    group: 'Produits',
                                                    label: 'Produits',
                                                    backgroundColor: '#4d65b9',
                                                    borderColor: '#4d65b9',
                                                    pointRadius: 0,
                                                    pointHoverRadius: 0,
                                                    fill: {
                                                        target: 'origin',
                                                        above: '#e4e8f4',
                                                    },
                                                    tension: 0.4,
                                                    data: props.row.original
                                                        .numberOfItemsChartData
                                                        .data,
                                                    labels: props.row.original
                                                        .numberOfItemsChartData
                                                        .labels,
                                                    yAxisID: 'y',
                                                },
                                            ],
                                            title: 'Produits',
                                            withDecimalsAxis1: false,
                                            axis1BeginAtZero: true,
                                        }),
                                }
                              : {},
                      )
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'numberOfR0DiscoveryItems',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'R0-Découverte',
                }),
            cell: (props) =>
                props.row.original.numberOfR0DiscoveryItems !== null
                    ? h(
                          NumberCell,
                          {
                              number: props.row.original
                                  .numberOfR0DiscoveryItems,
                          },
                          props.row.original.numberOfR0DiscoveryItemsChartData
                              ? {
                                    actions: () =>
                                        h(DisplayLineChartAction, {
                                            labels: props.row.original
                                                .numberOfR0DiscoveryItemsChartData
                                                .labels,
                                            datasets: [
                                                {
                                                    group: 'R0-Découverte',
                                                    label: 'R0-Découverte',
                                                    backgroundColor: '#4d65b9',
                                                    borderColor: '#4d65b9',
                                                    pointRadius: 0,
                                                    pointHoverRadius: 0,
                                                    fill: {
                                                        target: 'origin',
                                                        above: '#e4e8f4',
                                                    },
                                                    tension: 0.4,
                                                    data: props.row.original
                                                        .numberOfR0DiscoveryItemsChartData
                                                        .data,
                                                    labels: props.row.original
                                                        .numberOfR0DiscoveryItemsChartData
                                                        .labels,
                                                    yAxisID: 'y',
                                                },
                                            ],
                                            title: 'R0-Découverte',
                                            withDecimalsAxis1: false,
                                            axis1BeginAtZero: true,
                                        }),
                                }
                              : {},
                      )
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'numberOfR1ProfitableItems',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'R1-Rentable',
                }),
            cell: (props) =>
                props.row.original.numberOfR1ProfitableItems !== null
                    ? h(
                          NumberCell,
                          {
                              number: props.row.original
                                  .numberOfR1ProfitableItems,
                          },
                          props.row.original.numberOfR1ProfitableItemsChartData
                              ? {
                                    actions: () =>
                                        h(DisplayLineChartAction, {
                                            labels: props.row.original
                                                .numberOfR1ProfitableItemsChartData
                                                .labels,
                                            datasets: [
                                                {
                                                    group: 'R1-Rentable',
                                                    label: 'R1-Rentable',
                                                    backgroundColor: '#4d65b9',
                                                    borderColor: '#4d65b9',
                                                    pointRadius: 0,
                                                    pointHoverRadius: 0,
                                                    fill: {
                                                        target: 'origin',
                                                        above: '#e4e8f4',
                                                    },
                                                    tension: 0.4,
                                                    data: props.row.original
                                                        .numberOfR1ProfitableItemsChartData
                                                        .data,
                                                    labels: props.row.original
                                                        .numberOfR1ProfitableItemsChartData
                                                        .labels,
                                                    yAxisID: 'y',
                                                },
                                            ],
                                            title: 'R1-Rentable',
                                            withDecimalsAxis1: false,
                                            axis1BeginAtZero: true,
                                        }),
                                }
                              : {},
                      )
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'numberOfR2NotProfitableProfitableSiteItems',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'R2-PasRentable<br/>RentableSite',
                }),
            cell: (props) =>
                props.row.original
                    .numberOfR2NotProfitableProfitableSiteItems !== null
                    ? h(
                          NumberCell,
                          {
                              number: props.row.original
                                  .numberOfR2NotProfitableProfitableSiteItems,
                          },
                          props.row.original
                              .numberOfR2NotProfitableProfitableSiteItemsChartData
                              ? {
                                    actions: () =>
                                        h(DisplayLineChartAction, {
                                            labels: props.row.original
                                                .numberOfR2NotProfitableProfitableSiteItemsChartData
                                                .labels,
                                            datasets: [
                                                {
                                                    group: 'R2-PasRentable-RentableSite',
                                                    label: 'R2-PasRentable-RentableSite',
                                                    backgroundColor: '#4d65b9',
                                                    borderColor: '#4d65b9',
                                                    pointRadius: 0,
                                                    pointHoverRadius: 0,
                                                    fill: {
                                                        target: 'origin',
                                                        above: '#e4e8f4',
                                                    },
                                                    tension: 0.4,
                                                    data: props.row.original
                                                        .numberOfR2NotProfitableProfitableSiteItemsChartData
                                                        .data,
                                                    labels: props.row.original
                                                        .numberOfR2NotProfitableProfitableSiteItemsChartData
                                                        .labels,
                                                    yAxisID: 'y',
                                                },
                                            ],
                                            title: 'R2-PasRentable-RentableSite',
                                            withDecimalsAxis1: false,
                                            axis1BeginAtZero: true,
                                        }),
                                }
                              : {},
                      )
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'numberOfR3NotProfitableNotProfitableSiteItems',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'R3-PasRentable<br/>PasRentableSite',
                }),
            cell: (props) =>
                props.row.original
                    .numberOfR3NotProfitableNotProfitableSiteItems !== null
                    ? h(
                          NumberCell,
                          {
                              number: props.row.original
                                  .numberOfR3NotProfitableNotProfitableSiteItems,
                          },
                          props.row.original
                              .numberOfR3NotProfitableNotProfitableSiteItemsChartData
                              ? {
                                    actions: () =>
                                        h(DisplayLineChartAction, {
                                            labels: props.row.original
                                                .numberOfR3NotProfitableNotProfitableSiteItemsChartData
                                                .labels,
                                            datasets: [
                                                {
                                                    group: 'R3-PasRentable-PasRentableSite',
                                                    label: 'R3-PasRentable-PasRentableSite',
                                                    backgroundColor: '#4d65b9',
                                                    borderColor: '#4d65b9',
                                                    pointRadius: 0,
                                                    pointHoverRadius: 0,
                                                    fill: {
                                                        target: 'origin',
                                                        above: '#e4e8f4',
                                                    },
                                                    tension: 0.4,
                                                    data: props.row.original
                                                        .numberOfR3NotProfitableNotProfitableSiteItemsChartData
                                                        .data,
                                                    labels: props.row.original
                                                        .numberOfR3NotProfitableNotProfitableSiteItemsChartData
                                                        .labels,
                                                    yAxisID: 'y',
                                                },
                                            ],
                                            title: 'R3-PasRentable-PasRentableSite',
                                            withDecimalsAxis1: false,
                                            axis1BeginAtZero: true,
                                        }),
                                }
                              : {},
                      )
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'numberOfRProblem',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'R-Probleme',
                }),
            cell: (props) =>
                props.row.original.numberOfRProblemSiteItems !== null
                    ? h(
                          NumberCell,
                          {
                              number: props.row.original
                                  .numberOfRProblemSiteItems,
                          },
                          props.row.original.numberOfRProblemItemsChartData
                              ? {
                                    actions: () =>
                                        h(DisplayLineChartAction, {
                                            labels: props.row.original
                                                .numberOfRProblemItemsChartData
                                                .labels,
                                            datasets: [
                                                {
                                                    group: 'R-Probleme',
                                                    label: 'R-Probleme',
                                                    backgroundColor: '#4d65b9',
                                                    borderColor: '#4d65b9',
                                                    pointRadius: 0,
                                                    pointHoverRadius: 0,
                                                    fill: {
                                                        target: 'origin',
                                                        above: '#e4e8f4',
                                                    },
                                                    tension: 0.4,
                                                    data: props.row.original
                                                        .numberOfRProblemItemsChartData
                                                        .data,
                                                    labels: props.row.original
                                                        .numberOfRProblemItemsChartData
                                                        .labels,
                                                    yAxisID: 'y',
                                                },
                                            ],
                                            title: 'R-Probleme',
                                            withDecimalsAxis1: false,
                                            axis1BeginAtZero: true,
                                        }),
                                }
                              : {},
                      )
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'numberOfRExcluded',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'R-Exclu',
                }),
            cell: (props) =>
                props.row.original.numberOfRExcludedItems !== null
                    ? h(
                          NumberCell,
                          {
                              number: props.row.original.numberOfRExcludedItems,
                          },
                          props.row.original.numberOfRExcludedItemsChartData
                              ? {
                                    actions: () =>
                                        h(DisplayLineChartAction, {
                                            labels: props.row.original
                                                .numberOfRExcludedItemsChartData
                                                .labels,
                                            datasets: [
                                                {
                                                    group: 'R-Exclu',
                                                    label: 'R-Exclu',
                                                    backgroundColor: '#4d65b9',
                                                    borderColor: '#4d65b9',
                                                    pointRadius: 0,
                                                    pointHoverRadius: 0,
                                                    fill: {
                                                        target: 'origin',
                                                        above: '#e4e8f4',
                                                    },
                                                    tension: 0.4,
                                                    data: props.row.original
                                                        .numberOfRExcludedItemsChartData
                                                        .data,
                                                    labels: props.row.original
                                                        .numberOfRExcludedItemsChartData
                                                        .labels,
                                                    yAxisID: 'y',
                                                },
                                            ],
                                            title: 'R-Exclu',
                                            withDecimalsAxis1: false,
                                            axis1BeginAtZero: true,
                                        }),
                                }
                              : {},
                      )
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'numberOfZombie',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Zombies<br/>PMax',
                }),
            cell: (props) =>
                props.row.original.numberOfZombieItems !== null
                    ? h(
                          NumberCell,
                          {
                              number: props.row.original.numberOfZombieItems,
                          },
                          props.row.original.numberOfZombieItemsChartData
                              ? {
                                    actions: () =>
                                        h(DisplayLineChartAction, {
                                            labels: props.row.original
                                                .numberOfZombieItemsChartData
                                                .labels,
                                            datasets: [
                                                {
                                                    group: 'Zombies PMax',
                                                    label: 'Zombies PMax',
                                                    backgroundColor: '#4d65b9',
                                                    borderColor: '#4d65b9',
                                                    pointRadius: 0,
                                                    pointHoverRadius: 0,
                                                    fill: {
                                                        target: 'origin',
                                                        above: '#e4e8f4',
                                                    },
                                                    tension: 0.4,
                                                    data: props.row.original
                                                        .numberOfZombieItemsChartData
                                                        .data,
                                                    labels: props.row.original
                                                        .numberOfZombieItemsChartData
                                                        .labels,
                                                    yAxisID: 'y',
                                                },
                                            ],
                                            title: 'Zombies PMax',
                                            withDecimalsAxis1: false,
                                            axis1BeginAtZero: true,
                                        }),
                                }
                              : {},
                      )
                    : null,
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'generatedAt',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Généré le',
                }),
            cell: (props) =>
                props.row.original.generatedAt
                    ? h(TextCell, {
                          text: props.row.original.generatedAt,
                      })
                    : null,
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                size: 'small',
                                title: 'Modifier',
                                href: route(
                                    'front.projectManagement.sea.productManagement',
                                    {
                                        tenant: tenant.id,
                                        googleAdsProductManagement:
                                            props.row.original.id,
                                    },
                                ),
                                icon: PencilSimpleIcon,
                                square: true,
                            }),
                            h(
                                GenerateProductManagementFeedAction,
                                {
                                    tenant: tenant,
                                    googleAdsProductManagement:
                                        props.row.original,
                                    callback: () => updateData(),
                                },
                                {
                                    button: ({ action, loading }) =>
                                        h(ActionButton, {
                                            size: 'small',
                                            title: 'Générer le flux produit + CPC',
                                            callback: () => action(),
                                            loading,
                                            square: true,
                                            icon: ArrowsClockwiseIcon,
                                        }),
                                },
                            ),
                            h(
                                UpdateCpcProductManagementAction,
                                {
                                    tenant: tenant,
                                    googleAdsProductManagement:
                                        props.row.original,
                                    callback: () => updateData(),
                                },
                                {
                                    button: ({ action, loading }) =>
                                        h(ActionButton, {
                                            size: 'small',
                                            title: 'Mettre à jour les CPC',
                                            callback: () => action(),
                                            loading,
                                            square: true,
                                            icon: CurrencyEurIcon,
                                        }),
                                },
                            ),
                            h(
                                ResetProductManagementItemsAction,
                                {
                                    tenant: tenant,
                                    googleAdsProductManagement:
                                        props.row.original,
                                    callback: () => updateData(),
                                },
                                {
                                    button: ({ action, loading }) =>
                                        h(ActionButton, {
                                            size: 'small',
                                            title: 'Réinitialiser',
                                            callback: () => action(),
                                            loading,
                                            square: true,
                                            icon: RecycleIcon,
                                        }),
                                },
                            ),
                        ],
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.sea.postProductManagements', {
        tenant: tenant.id,
    }),
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
        force-scroll
    >
        <template #actions>
            <ActionButton
                size="small"
                :href="
                    route('front.projectManagement.sea.productManagement', {
                        tenant: tenant.id,
                    })
                "
                :left-icon="PlusIcon"
            >
                Ajouter
            </ActionButton>
        </template>
    </DataTableLayout>
</template>
